import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { RecruitmentTabs } from '../components/RecruitmentTabs';
import { OPENING_STATUS_LABEL, type OpeningStatus } from '../constants';
import { useDeleteOpening, useOpenings } from '../queries';
import type { JobOpening } from '../types';

const STATUS_VARIANT: Record<OpeningStatus, 'secondary' | 'outline' | 'destructive'> = {
  open: 'secondary',
  on_hold: 'outline',
  closed: 'outline',
  filled: 'secondary',
};

export function OpeningsPage() {
  const navigate = useNavigate();
  const { data, isPending, isError, error, isFetching } = useOpenings();
  const remove = useDeleteOpening();
  const [toDelete, setToDelete] = useState<JobOpening | null>(null);

  const openings = data?.results ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Recruitment</h1>
          <p className="text-muted-foreground text-sm">Pipeline from opening to hire.</p>
        </div>
        <Button asChild>
          <Link to="/hrm/recruitment/new">
            <Plus className="size-4" /> New opening
          </Link>
        </Button>
      </div>

      <RecruitmentTabs />

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load openings.'}
        </p>
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Headcount</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : openings.length ? (
                openings.map((o) => (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/hrm/recruitment/${o.public_id}`)}
                  >
                    <TableCell className="font-medium">{o.title}</TableCell>
                    <TableCell className="text-muted-foreground">{o.department || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[o.status]}>{OPENING_STATUS_LABEL[o.status]}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{o.openings_count}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Edit opening"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/hrm/recruitment/${o.public_id}/edit`);
                          }}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Delete opening"
                          onClick={(e) => {
                            e.stopPropagation();
                            setToDelete(o);
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground text-center">
                    No openings yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete opening?</AlertDialogTitle>
            <AlertDialogDescription>
              “{toDelete?.title}” will be removed. Existing candidates linked to it will keep their record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
